import { useEffect, useState, useCallback } from "react";
import { AuthAPI } from "../api";
import { Link, useNavigate } from "react-router-dom";
import DateTimePicker from "../components/DateTimePicker";
import { useAuth } from "../context/AuthContext";
import Dialog from "../components/Dialog";

const Mentors = () => {
  const [mentors, setMentors] = useState([]);
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated, isValidating } = useAuth();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({ colleges: [], majors: [], graduation_years: [] });
  const [error, setError] = useState(false);

  // Slot request modal state
  const [requestModal, setRequestModal] = useState(null); // { slot } | null
  const [requestStartTime, setRequestStartTime] = useState(null);
  const [requestDuration, setRequestDuration] = useState(20);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);

  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async (nextSearch = search, nextFilters = filters) => {
    setError(false);
    try {
      const query = [];
      if (nextSearch) query.push(`search=${encodeURIComponent(nextSearch)}`);
      Object.keys(nextFilters).forEach((key) => {
        if (nextFilters[key]) query.push(`${key}=${encodeURIComponent(nextFilters[key])}`);
      });

      const url = query.length ? `mentors/?${query.join("&")}` : "mentors/";
      const res = await AuthAPI.get(url);
      setMentors(res.data);

      const slotRes = await AuthAPI.get("availability/");
      const grouped = {};
      slotRes.data.forEach((slot) => {
        if (!grouped[slot.mentor.id]) grouped[slot.mentor.id] = [];
        grouped[slot.mentor.id].push(slot);
      });
      setSlots(grouped);
    } catch (err) {
      console.error("Error fetching mentors:", err);
      setError(true);
    }
  }, [filters, search]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await AuthAPI.get("mentors/filters/");
      setOptions(res.data);
    } catch (err) {
      console.error("Error fetching filter options:", err);
    } 
  }, []);

  const loadMentorDirectory = useCallback(async (nextSearch = search, nextFilters = filters) => {
    setLoading(true);
    await Promise.all([fetchData(nextSearch, nextFilters), fetchFilterOptions()]);
    setLoading(false);
  }, [fetchData, fetchFilterOptions, filters, search]);

  useEffect(() => {
    if (isAuthenticated) {
      loadMentorDirectory();
    } else if (!isValidating) {
      setLoading(false);
    }
  }, [isAuthenticated, isValidating, loadMentorDirectory]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadMentorDirectory();
  };

  const openRequestModal = (slot) => {
    setRequestModal({ slot });
    setRequestStartTime(new Date(slot.start_time));
    setRequestDuration(20);
    setRequestMessage("");
  };

  const closeRequestModal = () => {
    setRequestModal(null);
    setRequestStartTime(null);
    setRequestDuration(20);
    setRequestMessage("");
  };

  const handleSendRequest = async () => {
    if (!requestModal) return;
    const { slot } = requestModal;
    const start = requestStartTime || new Date(slot.start_time);
    const end = new Date(start.getTime() + requestDuration * 60000);
    setRequestLoading(true);
    try {
      await AuthAPI.post("session-requests/", {
        mentor: slot.mentor.id,
        requested_start_time: start.toISOString(),
        requested_end_time: end.toISOString(),
        message: requestMessage,
      });
      showToast(
        `Request sent to ${slot.mentor.profile.user.first_name} for ${new Date(slot.start_time).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}.`
      );
      closeRequestModal();
    } catch (err) {
      console.error("Request failed", err);
      showToast("Failed to send request. Please try again.", "error");
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="h-8 w-8 text-[#282E6A] animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );
  }

  const selectClass = "w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#282E6A] focus:border-[#282E6A] focus:outline-none";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

      {/* Toast */}
      {toast && (
        <div className={`mb-5 flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium ${
          toast.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}>
          {toast.type === "success" ? (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          {toast.message}
        </div>
      )}

      {/* Page header */}
      <div className="mb-6 overflow-hidden rounded-[28px] border border-[#dfe4f2] bg-white/80 shadow-[0_24px_60px_-42px_rgba(23,30,67,0.35)] backdrop-blur-sm">
        <div className="grid gap-6 px-6 py-7 lg:grid-cols-[1.2fr_0.8fr] lg:px-8">
          <div>
            <span className="inline-flex rounded-full border border-[#cfd5ee] bg-[#eef0fa] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A]">
              Mentor directory
            </span>
            <h1 className="mt-4 max-w-2xl text-3xl leading-tight text-[#111425] sm:text-4xl">
              Find a mentor whose lived experience actually matches your decision.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5d647f] sm:text-base">
              Browse current students and alumni by college, major, and graduation year, then request either an open slot or a custom time.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-[22px] border border-[#e0e4f2] bg-[#f7f8fc] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5d647f]">What changes here</p>
              <p className="mt-2 text-sm leading-6 text-[#111425]">You can filter by context, see live availability, and send a direct request without a consultant in the middle.</p>
            </div>
            {!isAuthenticated && (
              <div className="rounded-[22px] border border-[#ecd7b0] bg-[#f7f2e6] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8d6a2a]">Unlock booking</p>
                <p className="mt-2 text-sm leading-6 text-[#111425]">Sign in to view availability, request times, and open mentor profiles fully.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      {isAuthenticated && (
        <form
          onSubmit={handleSearch}
          className="mb-7 p-5 bg-white border border-gray-200 rounded-xl shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">
                Search
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Name, college, or major"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#282E6A] focus:border-[#282E6A] focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#282E6A] hover:bg-[#1e2458] text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setFilters({});
                    loadMentorDirectory("", {});
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">College</label>
              <select name="college_name" value={filters.college_name || ""} onChange={handleFilterChange} className={selectClass}>
                <option value="">All colleges</option>
                {options.colleges?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Major</label>
              <select name="major" value={filters.major || ""} onChange={handleFilterChange} className={selectClass}>
                <option value="">All majors</option>
                {options.majors?.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Grad year</label>
              <select name="graduation_year" value={filters.graduation_year || ""} onChange={handleFilterChange} className={selectClass}>
                <option value="">Any year</option>
                {options.graduation_years?.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </form>
      )}

      {/* Mentor grid */}
      {!isAuthenticated ? (
        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900">Mentor profiles are ready</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600">
            Sign in to browse live mentor availability, request sessions, and book custom times directly from the calendar.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="rounded-lg bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#1e2458]"
            >
              Log in
            </button>
            <button
              type="button"
              onClick={() => navigate("/register")}
              className="rounded-lg border border-[#282E6A] px-5 py-2.5 text-sm font-medium text-[#282E6A] transition-colors hover:bg-[#eef0fa]"
            >
              Create account
            </button>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-[26px] border border-red-200 bg-white p-8 shadow-sm">
          <div className="mx-auto max-w-xl text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm8.25-3.75a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-[#111425]">Mentor profiles did not load</h2>
            <p className="mt-2 text-sm leading-7 text-[#5d647f]">
              The directory is unavailable right now. Retry the request before assuming there are no mentors.
            </p>
            <button
              type="button"
              onClick={() => {
                loadMentorDirectory();
              }}
              className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
            >
              Retry directory
            </button>
          </div>
        </div>
      ) : mentors.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <svg className="mx-auto mb-3 h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
          <p className="text-base font-medium text-gray-500">No mentors found</p>
          <p className="mt-1 text-sm">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {mentors.map((mentor) => {
            const availableSlots = slots[mentor.id]
              ? slots[mentor.id].filter((slot) => !slot.is_booked)
              : [];

            return (
              <div
                key={mentor.id}
                className="flex gap-4 rounded-[24px] border border-[#dfe4f2] bg-white/92 p-5 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.4)] transition-shadow hover:shadow-[0_28px_70px_-42px_rgba(23,30,67,0.45)]"
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {mentor.profile_picture ? (
                    <img
                      src={mentor.profile_picture}
                      alt={`${mentor.profile.user.first_name} ${mentor.profile.user.last_name}`}
                      className="h-20 w-20 rounded-full border border-gray-200 object-cover"
                      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                    />
                  ) : null}
                  <div
                    className="h-20 w-20 items-center justify-center rounded-full border border-gray-200 bg-gray-100"
                    style={{ display: mentor.profile_picture ? "none" : "flex" }}
                  >
                    <svg viewBox="0 0 24 24" className="h-12 w-12 text-gray-400" fill="currentColor">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" />
                    </svg>
                  </div>
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-gray-900">
                      {mentor.profile.user.first_name} {mentor.profile.user.last_name}
                    </h2>
                    {mentor.profile.user.is_verified && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>

                  <p className="mt-1.5 text-sm font-medium text-gray-700">
                    {mentor.major} · {mentor.college_name}
                  </p>
                  {mentor.graduation_year && (
                    <p className="text-xs text-gray-500">
                      Class of {mentor.graduation_year}
                    </p>
                  )}

                  {mentor.current_position && (
                    <p className="mt-1 text-sm text-gray-600">
                      {mentor.current_position}
                      {mentor.company ? ` at ${mentor.company}` : ""}
                    </p>
                  )}

                  {mentor.expertise && (
                    <p className="mt-1.5 line-clamp-2 text-sm text-gray-500">
                      {mentor.expertise}
                    </p>
                  )}

                  {/* Availability */}
                  <details className="mt-3">
                    <summary className="flex w-fit list-none cursor-pointer items-center gap-1 text-sm font-medium text-[#282E6A] hover:text-[#1e2458]">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                      </svg>
                      View available times ({availableSlots.length})
                    </summary>
                    <div className="mt-2 pl-1">
                      {availableSlots.length === 0 ? (
                        <p className="text-sm text-gray-400">No open slots right now.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {availableSlots.map((slot) => {
                            const start = new Date(slot.start_time);
                            const end = new Date(slot.end_time);
                            const durationMins = Math.round((end - start) / 60000);
                            return (
                              <li key={slot.id} className="flex items-center justify-between gap-3">
                                <span className="text-xs text-gray-600">
                                  {start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}{" "}
                                  {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                  {" – "}
                                  {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                  <span className="ml-1.5 text-gray-400">({durationMins} min)</span>
                                </span>
                                <button
                                  onClick={() => openRequestModal(slot)}
                                  className="flex-shrink-0 rounded-md bg-[#282E6A] px-3 py-1 text-xs text-white transition-colors hover:bg-[#1e2458]"
                                >
                                  Request
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </details>

                  {/* Request custom time */}
                  <Link
                    to={`/calendar/${mentor.id}`}
                    className="mt-3 inline-flex rounded-lg border border-[#282E6A] px-4 py-1.5 text-xs font-medium text-[#282E6A] transition-colors hover:bg-[#eef0fa]"
                  >
                    Request a custom time
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Slot Request Modal ===== */}
      {requestModal && (
        <Dialog
          isOpen={!!requestModal}
          onClose={closeRequestModal}
          panelClassName="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        >
          {({ titleId }) => (
            <div>
              <h3 id={titleId} className="mb-1 text-lg font-semibold text-gray-900">Request session</h3>
              <p className="mb-5 text-sm text-gray-500">
                Your request will be sent to the mentor for approval.
              </p>

              <div className="mb-4">
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Start time
                </label>
                <DateTimePicker
                  value={requestStartTime}
                  onChange={(date) => setRequestStartTime(date)}
                />
              </div>

              <div className="mb-1.5">
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Duration</label>
                <select
                  value={requestDuration}
                  onChange={(e) => setRequestDuration(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
                >
                  <option value={20}>20 minutes</option>
                  <option value={40}>40 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>

              <p className="mb-5 text-xs text-gray-400">
                Session ends at{" "}
                <span className="font-medium text-gray-600">
                  {new Date(
                    (requestStartTime || new Date(requestModal.slot.start_time)).getTime() +
                    requestDuration * 60000
                  ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </p>

              <div className="mb-5">
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Message to mentor <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. I'd love to learn about your experience at MIT, specifically around CS admissions…"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  className="w-full resize-none rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition focus:border-[#282E6A] focus:outline-none focus:ring-2 focus:ring-[#282E6A]"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSendRequest}
                  disabled={requestLoading}
                  className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors ${
                    requestLoading
                      ? "cursor-not-allowed bg-gray-200 text-gray-400"
                      : "bg-[#282E6A] text-white hover:bg-[#1e2458]"
                  }`}
                  data-autofocus="true"
                >
                  {requestLoading ? "Sending…" : "Send request"}
                </button>
                <button
                  onClick={closeRequestModal}
                  disabled={requestLoading}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
};

export default Mentors;
